import ErrorMessage from '@/components/common/ErrorMessage';
import Layout from '@/components/Layout/Layout';
import { errorTypes } from '@/utility/validation';
import React, { useEffect, useState } from 'react';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import ViewDocumentModal from '@/components/common/ViewDocumentModal';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import { IDocument } from '@/interfaces/user.model';
import Label from '@/components/ui/Label';
import {
  handleFormDocuments,
  showToastNotification,
  statesData,
} from '@/utility/helper';
import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import PrimaryInput from '@/components/ui/PrimaryInput';
import BackButton from '@/components/ui/BackButton';
import CardLayout from '@/components/Layout/CardLayout';
import Ckeditor from '@/components/common/CkeEditor';
import { ICompanyResponse } from '@/interfaces/company.model';
import FieldLayout from '@/components/Layout/FieldLayout';
import FormButtons from '@/components/common/Form/FormButtons';
import FormHead from '@/components/common/Form/FormHead';
import { RequiredDocs } from '@/utility/enum';
import { IDeleteResponse, INewDocuments } from '@/interfaces/common.model';
import Document from '@/components/common/Document';
import FullPageLoader from '@/components/loader/FullPageLoader';
interface IAddCompanyForm {
  name: string;
  shortName: string;
  email: string;
  secondaryEmail: string;
  phone: string;
  secondaryPhone: string;
  gst: string;
  pan: string;
  address: string;
  city: string;
  pinCode: string;
  documentType: keyof INewDocuments | null;
  state: string;
  tANDc: string;
}

const AddCompany: React.FC = () => {
  const method = useForm<IAddCompanyForm>({ mode: 'all' });
  const {
    handleSubmit,
    register,
    setValue,
    reset,
    formState: { errors },
    control,
  } = method;
  const navigate = useNavigate();
  const { id } = useParams();
  const [isDocumentOpen, setIsDocumentOpen] = useState<boolean>(false);
  const [isConfirModal, setIsConfirmModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [documentError, setDocumentError] = useState<string>('');
  const [existingDocuments, setExistingDocuments] = useState<IDocument[]>([]);
  const [selectedDocument, setSelectedDocument] =
    useState<Partial<IDocument> | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newDocuments, setNewDocuments] = useState<INewDocuments[]>([]);

  // const handleAddDocument = () => {
  //   const docType = watch('documentType');
  //   if (!docType) return;
  //   const selectedDocument = watch('documentType') as keyof INewDocuments;
  //   if (!selectedDocument) return;
  //   if (
  //     selectedDocument &&
  //     !newDocuments?.some((doc) => doc[selectedDocument]) &&
  //     !existingDocuments.some((doc) => doc?.name === selectedDocument)
  //   ) {
  //     setNewDocuments((prev) => [...prev, { [selectedDocument]: null }]);
  //     setValue('documentType', null);
  //   }
  // };

  const getCompanyDetails = async () => {
    setIsLoading(true);
    try {
      const response: ICompanyResponse = await callApi(
        API_URL + API_ENDPOINT.getCompany + '?companyId=' + id,
        { method: 'GET' },
      );
      const data = response?.data?.company[0];
      reset({
        name: data.name,
        shortName: data.shortName,
        email: data.email,
        phone: data.phone,
        secondaryEmail: data.secondaryEmail,
        secondaryPhone: data.secondaryPhone,
        gst: data.gst,
        tANDc: data.tANDc,
        address: data.address,
        pinCode: data.pinCode,
        state: data.state,
        pan: data.pan,
        city: data.city,
      });
      setExistingDocuments(data?.documents);
      // setCompanyDetails(response?.data?.company[0]);
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };
  const checkRequiredDocuments = () => {
    for (const docType of Object.values(RequiredDocs)) {
      const existsInNew = newDocuments?.some((doc) => {
        const key = docType as keyof INewDocuments;
        return (
          Object.prototype.hasOwnProperty.call(doc, docType) &&
          doc[key] !== null
        );
      });

      const existsInExisting = existingDocuments.some(
        (doc) => doc.name === docType && doc.url, // Assuming each `existingDocument` has `name` and `url`
      );

      if (!existsInNew && !existsInExisting) {
        const type = String(docType);
        setDocumentError(`${type?.replaceAll('_', ' ')} is required`);
        return false;
      }
    }
    setDocumentError('');
    return true;
  };
  const onSubmit: SubmitHandler<IAddCompanyForm> = async (data) => {
    if (!checkRequiredDocuments()) {
      showToastNotification('danger', 'All Three Documents are Required');
      return;
    }
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      const dataKey = key as keyof IAddCompanyForm;
      const value = data[dataKey];
      if (value) {
        formData.append(key, value);
      }
    });
    handleFormDocuments(formData, newDocuments);
    // formData.forEach((key, data) => {
    // });
    setIsLoading(true);
    try {
      const response: IDeleteResponse = await callApi(
        id
          ? API_URL + API_ENDPOINT.addCompany + `?companyId=${id}`
          : API_URL + API_ENDPOINT.addCompany,
        {
          method: 'POST',
          multipart: true,
          data: formData,
        },
      );
      if (response?.status) {
        showToastNotification('success', response?.message);
        navigate(-1);
        reset();
      }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };
  const toggleModal = () => {
    setIsDocumentOpen(!isDocumentOpen);
  };
  const toggleConfirmModal = () => {
    setIsConfirmModal(!isConfirModal);
  };
  // const handleRemoveNewDocument = (key: string) => {
  //   setNewDocuments((prev) =>
  //     prev?.filter((doc) => !Object.prototype.hasOwnProperty.call(doc, key)),
  //   );
  // };
  const handleDeleteDocument = async () => {
    try {
      const response: IDeleteResponse = await callApi(
        `${API_URL + API_ENDPOINT.deleteDocument}?documentId=${selectedId}`,
        { method: 'GET' },
      );
      toggleConfirmModal();
      if (response?.status) {
        setExistingDocuments((prev) =>
          prev.filter((doc) => doc.id !== selectedId),
        );
        showToastNotification('success', response.message);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    }
  };
  // const handleDocumentClick = (item: IDocument) => {
  //   const docUrl = {
  //     url: item?.url,
  //   };
  //   setSelectedDocument(docUrl);
  //   // setIsDocumentOpen(true);
  //   toggleModal();
  // };
  // const handleFileChange = (key: keyof INewDocuments, file: File | null) => {
  //   if (!file) return;
  //   setNewDocuments((prev) =>
  //     prev?.map((doc) => (doc[key] !== undefined ? { [key]: file } : doc)),
  //   );
  //   if (key == 'Company_Logo') {
  //     setDocumentError('');
  //   }
  // };
  // const actionHandler = (id: string) => {
  //   setSelectedId(id);
  //   toggleConfirmModal();
  // };
  useEffect(() => {
    if (id) {
      getCompanyDetails();
    }
  }, [id, getCompanyDetails]);
  return (
    <Layout>
      {isLoading ? (
        <FullPageLoader />
      ) : (
        <div className="content container-fluid">
          <BackButton isEdit={id != undefined} label={'Company'} />
          <FormProvider {...method}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardLayout>
                <div className="row">
                  <div className="row">
                    <FormHead heading="Company Details" />
                    <FieldLayout fieldStyle="col-12 col-lg-6">
                      <PrimaryInput
                        type={'text'}
                        id="name"
                        placeholder="Enter name of company"
                        label="Name"
                        errors={errors?.name?.message}
                        required={true}
                        {...register('name', {
                          ...errorTypes.required('Company Name'),
                        })}
                      />
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-12 col-lg-6">
                      <PrimaryInput
                        label="Short Name"
                        required={true}
                        errors={errors?.shortName?.message}
                        type="text"
                        id="shortName"
                        placeholder="Enter short name of company"
                        {...register('shortName', {
                          ...errorTypes.required('Company shortName'),
                        })}
                      />
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-12 col-lg-6">
                      <PrimaryInput
                        required={true}
                        label={'Email'}
                        type="text"
                        id="email"
                        errors={errors?.email?.message}
                        placeholder="Enter email of company"
                        {...register('email', {
                          ...errorTypes.required('Email'),
                          ...errorTypes.email,
                          onChange: (e) => {
                            const lowercaseValue = e.target.value.toLowerCase();
                            setValue('email', lowercaseValue);
                          },
                        })}
                      />
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-12 col-lg-6">
                      <PrimaryInput
                        label="Secondary Email"
                        errors={errors?.secondaryEmail?.message}
                        type="text"
                        id="secondaryEmail"
                        placeholder="Enter secondary email of company"
                        {...register('secondaryEmail', {
                          validate: (value) =>
                            !value ||
                            errorTypes.email?.pattern?.value?.test(value) ||
                            errorTypes.email?.pattern?.message ||
                            'Invalid email',
                          onChange: (e) => {
                            const lowercaseValue = e.target.value.toLowerCase();
                            setValue('secondaryEmail', lowercaseValue);
                          },
                        })}
                      />
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-12 col-lg-6">
                      <PrimaryInput
                        type="text"
                        id="phone"
                        label="Contact No."
                        required={true}
                        errors={errors?.phone?.message}
                        placeholder="Enter contact number"
                        {...register('phone', {
                          ...errorTypes.required('Company Contact Number'),
                          ...errorTypes.phone,
                        })}
                        onChange={(e) => {
                          e.target.value = e.target.value.replace(
                            /[^0-9]/g,
                            '',
                          );
                        }}
                      />
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-12 col-lg-6">
                      <PrimaryInput
                        type="text"
                        id="secondaryPhone"
                        label="Secondary Phone"
                        className="form-control"
                        errors={errors?.secondaryPhone?.message}
                        placeholder="Enter secondary phone number"
                        {...register('secondaryPhone', {
                          ...errorTypes.phone,
                        })}
                        onChange={(e) => {
                          e.target.value = e.target.value.replace(
                            /[^0-9]/g,
                            '',
                          );
                        }}
                      />
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-12 col-lg-6">
                      <PrimaryInput
                        type="text"
                        label=" GST No."
                        id="gst"
                        required={true}
                        errors={errors?.gst?.message}
                        placeholder="Enter GST number of company"
                        {...register('gst', {
                          ...errorTypes.required('Company GST'),
                          ...errorTypes.gstNumber,
                        })}
                      />
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-12 col-lg-6">
                      <PrimaryInput
                        type="text"
                        label={'PAN No.'}
                        required={true}
                        placeholder="PAN Number"
                        errors={errors?.pan?.message}
                        id="panNum"
                        {...register('pan', {
                          ...errorTypes.required('Company Pan Card'),
                          ...errorTypes.pan,
                        })}
                        onChange={(e) => {
                          e.target.value = e.target.value.toUpperCase();
                        }}
                      />
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-12 col-lg-6">
                      <PrimaryInput
                        type="text"
                        required={true}
                        label="Address"
                        errors={errors?.address?.message}
                        id="address"
                        placeholder="Enter your address"
                        {...register('address', {
                          ...errorTypes.required('Address'),
                        })}
                      />
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-12 col-lg-6">
                      <Label id="state" required={true}>
                        State
                      </Label>
                      <select
                        className="form-select form-control"
                        {...register('state', {
                          ...errorTypes.required('State'),
                        })}
                      >
                        <option value="" className="d-none">
                          Select state
                        </option>
                        {statesData?.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                      <ErrorMessage message={errors?.state?.message} />
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-12 col-lg-6">
                      <PrimaryInput
                        errors={errors?.city?.message}
                        label="City"
                        type="text"
                        id="city"
                        required={true}
                        placeholder="Enter your City"
                        {...register('city', {
                          ...errorTypes.required('City'),
                        })}
                      />
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-12 col-lg-6">
                      <PrimaryInput
                        type="text"
                        label={'Zip Code'}
                        required={true}
                        errors={errors?.pinCode?.message}
                        id="pincode"
                        className="form-control "
                        placeholder="Enter your Zip Code"
                        {...register('pinCode', {
                          ...errorTypes.required('Zip Code'),
                          ...errorTypes.pinCode,
                          onChange: async (e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            setValue('pinCode', value);
                            if (value.length === 6) {
                              try {
                                const response = await fetch(
                                  `https://api.postalpincode.in/pincode/${value}`,
                                );
                                const data = await response.json();
                                if (
                                  data[0].Status === 'Success' &&
                                  data[0].PostOffice.length > 0
                                ) {
                                  let state = data[0].PostOffice[0].State;
                                  state = state.replace(/\s/g, '');
                                  if (statesData.includes(state)) {
                                    setValue('state', state);
                                  }
                                  setValue(
                                    'city',
                                    data[0].PostOffice[0].District,
                                  );
                                }
                              } catch (error) {
                                console.error('Pincode lookup failed', error);
                              }
                            }
                          },
                        })}
                      />
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-12 col-lg-12">
                      <Label id={'tANDc'} required={true}>
                        Terms & Conditions
                      </Label>
                      <Controller
                        name="tANDc"
                        control={control}
                        rules={{
                          required: 'Terms & Conditions are required',
                        }}
                        render={({ field }) => (
                          <Ckeditor
                            {...field}
                            value={field.value}
                            onChange={field.onChange}
                            error={errors?.tANDc?.message}
                          />
                        )}
                      />
                    </FieldLayout>
                    <Document
                      setDocumentError={setDocumentError}
                      docs={RequiredDocs}
                      newDocuments={newDocuments}
                      existingDocuments={existingDocuments}
                      toggleModal={toggleModal}
                      toggleConfirmModal={toggleConfirmModal}
                      setSelectedId={setSelectedId}
                      setNewDocuments={setNewDocuments}
                      setSelectedDocument={setSelectedDocument}
                    />
                  </div>
                  <ErrorMessage message={documentError} />
                  <FormButtons
                    isLoading={isLoading}
                    buttonStyle="gap-3 mt-4 mb-3"
                  >
                    {id ? 'Edit Company' : 'Add Company'}
                  </FormButtons>
                </div>
              </CardLayout>
            </form>
          </FormProvider>
        </div>
      )}
      <ViewDocumentModal
        isOpen={isDocumentOpen}
        toggle={toggleModal}
        selectedDocument={selectedDocument}
      />
      <ConfirmModal
        isOpen={isConfirModal}
        toggle={toggleConfirmModal}
        submitHandler={handleDeleteDocument}
        isLoading={isLoading}
      />
    </Layout>
  );
};
export default AddCompany;
